
import { mapGetters } from 'vuex'
import { parsePhoneNumber } from 'awesome-phonenumber'
import { definePhoneValidation } from '@twago/flows'

export default {
  name: 'MobilePhoneSelector',

  props: {
    id: {
      type: String,
      default: 'phone'
    },
    value: {
      type: Object,
      required: true
    },
    errorMessage: {
      type: Array,
      default: null,
      required: false
    },
    label: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isValidateOnMounted: {
      type: Boolean,
      default: false
    },
    isLoginOrSignup: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isValid: true,
      iti: null,
      isActive: false,
      containerWidth: 0,
      inputHeight: 24,
      flagContainer: null,
      editedNumber: '',
      editedCode: ''
    }
  },

  computed: {
    ...mapGetters({
      countriesCodeList: 'mobile/countriesCodeList',
      mobileCountriesSettingsPrefList: 'mobile/mobileCountriesSettingsPrefList'
    }),

    validationRule() {
      if (!this.$attrs?.validation) {
        return this.errorMessage
      }

      const validationArray = [
        {
          name: 'code',
          validation: {
            required: true,
            requiredMessage: 'custom.flow.onb.validate.required.country.code'
          },
          value: this.editedCode
        },
        {
          name: this.$attrs.name,
          validation: this.$attrs.validation,
          value: this.editedNumber
        }
      ]

      return definePhoneValidation(validationArray, this.$i18n, this.isValid)
    },
    valueEditor: {
      get() {
        return this.value.number
      },
      set(val) {
        this.validateAndEmitPhoneObject(val)
      }
    },
    selectedCountryCode: {
      get() {
        return this.value.countryCode
      },
      set() {
        this.validateAndEmitPhoneObject(this.editedNumber)
      }
    },
    styles() {
      const styles = {
        '--primary': this.$vuetify.theme.themes.light.primary,
        '--secondary': 'rgba(0, 0, 0, 0.42)',
        '--labelColor': this.$vuetify.theme.themes.light.grey.darken2
      }

      this.setCssVariable(styles)

      return styles
    },
    isPhoneLength() {
      return !!this.valueEditor?.length
    }
  },

  async mounted() {
    const input = document.getElementById(this.id)

    if (input) {
      try {
        this.iti = await this.$intlTelInput(input, {
          initialCountry: this.selectedCountryCode,
          onlyCountries: this.countriesCodeList,
          countryOrder: this.mobileCountriesSettingsPrefList,
          autoPlaceholder: "'off'",
          useFullScreenPopup: false,
          nationalMode: false,
          separateDialCode: true,
          hiddenInput: function (telInputName) {
            return { phone: 'phone_full', country: 'country_code' }
          }
        })

        const code = this.iti?.getSelectedCountryData()
        this.editedNumber = this.value.number
        this.editedCode = code.dialCode || ''

        if (!this.editedCode && this.editedNumber) {
          this.validateAndEmitPhoneObject(this.editedNumber)
        }

        if (this.isValidateOnMounted) {
          this.validateAndEmitPhoneObject(this.editedNumber)
          setTimeout(() => {
            this.handleBlur()
          }, 300)
        }

        window.addEventListener('scroll', this.updateContainerWidthAndPosition)

        this.itiFlagContainer = document.getElementsByClassName('iti__country-container')[0]

        if (this.itiFlagContainer) {
          this.itiFlagContainer.addEventListener('click', this.updateContainerWidthAndPosition)
        }

        this.updateContainerWidthAndPosition()
      } catch (err) {
        console.log('err', err)
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.updateContainerWidthAndPosition)

    if (this.itiFlagContainer) {
      this.itiFlagContainer.removeEventListener('click', this.updateContainerWidthAndPosition)
    }

    this.iti?.destroy()
  },
  methods: {
    validateAndEmitPhoneObject(number) {
      const countryData = this.iti.getSelectedCountryData()

      this.editedNumber = number
      this.editedCode = countryData?.dialCode || ''

      const pn = parsePhoneNumber(number, { regionCode: countryData?.iso2?.toUpperCase() })
      this.isValid = !!pn?.valid

      if (countryData?.iso2) {
        this.iti.setCountry(countryData.iso2)
      }

      if (this.isValid || this.isLoginOrSignup) {
        this.$emit(
          'input',
          {
            code: countryData?.dialCode,
            countryCode: countryData?.iso2?.toUpperCase(),
            number: pn?.number?.significant || number
          },
          this.isValid
        )
      }
    },
    updateContainerWidthAndPosition() {
      const parentContainer = this.$refs.phoneNumber?.$el?.closest('.tel-input-number')

      if (parentContainer) {
        document.documentElement.style.setProperty(
          '--top',
          `${parentContainer.getBoundingClientRect()?.bottom - this.inputHeight}px`
        )
      }
    },
    handleBlur() {
      if (this.isValid) {
        this.$emit('blur', this.value)
      }
    },
    validate() {
      return this.$refs.phoneNumber.validate() && this.isValid
    },
    setCssVariable(styles) {
      if (typeof window !== 'undefined' && window.document) {
        Object.entries(styles).forEach(([key, value]) => {
          document?.documentElement?.style?.setProperty(key, value)
        })
      }
    }
  }
}
